import { motion, type Variants } from 'framer-motion';
import { forwardRef } from 'react';


import * as styles from './SuggestionsOverlay.sty.js';
import { SuggestionTips } from './SuggestionTips.js';
import { OverlayContainer } from '../../../core/components/OverlayContainer.js';
import { useOverlayStacking } from '../../../core/hooks/useOverlayStacking.js';
import { OverlayStackingProvider } from '../../../core/providers/OverlayStackingProvider.js';
import type { AriaLabelingProps } from '../../../core/types/a11y-props.js';
import type { DataTestId } from '../../../core/types/data-props.js';
import type { MaskingProps } from '../../../core/types/masking-props.js';
import type { WithChildren } from '../../../core/types/with-children.js';
import type { ModalAriaProps } from '../../../overlays/hooks/useOverlayWithTrigger.js';
import useOverlayTransition from '../hooks/useOverlayTransition.js';

/**
 * Accepted properties for FilterField suggestion overlay.
 * @public
 */
export interface SuggestionsOverlayProps
  extends AriaLabelingProps,
    WithChildren,
    DataTestId,
    MaskingProps {
  /**
   * Properties for connecting the overlay to the overlay trigger.
   * Includes styles and accessibility attributes.
   */
  overlayProps?: ModalAriaProps;
}

// Animation variants for the SuggestionsOverlay component
const suggestionsOverlayAnimationVariants: Variants = {
  inital: {
    opacity: 0.4,
    scaleY: 0.8,
  },
  default: {
    opacity: 1,
    scaleY: 1,
  },
  exit: {
    opacity: 0,
    scaleY: 0.8,
  },
};

/**
 * @public
 */
export const SuggestionsOverlay = forwardRef<
  HTMLDivElement,
  SuggestionsOverlayProps
>((props, forwardedRef) => {
  const {
    children,
    overlayProps,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;

  const overlayContainerProps = useOverlayStacking(undefined, 'dropdown');
  const transition = useOverlayTransition(false);

  return (
    <OverlayContainer
      className="suggestions-overlay"
      {...overlayContainerProps}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
    >
      <OverlayStackingProvider>
        <div
          ref={forwardedRef}
          data-testid={dataTestId}
          onPointerDown={(event) => {
            // Prevent default on the suggestions overlay to avoid filter field
            // losing focus when clicking on an entry in the overlay.
            event.preventDefault();
          }}
          {...overlayProps}
        >
          <motion.div
            className={styles.suggestionsOverlayWrapper}
            variants={suggestionsOverlayAnimationVariants}
            initial="inital"
            animate="default"
            exit="exit"
            transition={transition}
          >
            {children}
            <SuggestionTips />
          </motion.div>
        </div>
      </OverlayStackingProvider>
    </OverlayContainer>
  );
});
